import { SetStateAction, useAtom, WritableAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'src/common/useDebounce';

const DEFAULT_DEBOUNCE_MS = 500;

type useFiltersDebouncedIdProps = {
  appliedSearchText: string;
  onInputValueChanged?: (value: string) => void;
  onDebounceEnd: (value: string) => void;
};

const useFiltersSearchId = ({
  appliedSearchText,
  onInputValueChanged,
  onDebounceEnd,
}: useFiltersDebouncedIdProps) => {
  const debouncingInput = useRef(false);
  const [inputValue, setInputValue] = useState(appliedSearchText);

  const debouncedValue = useDebounce(inputValue, DEFAULT_DEBOUNCE_MS);

  const stopDebouncing = () => {
    onDebounceEnd(inputValue);
    debouncingInput.current = false;
  };

  const onChange = useCallback((value: string) => {
    debouncingInput.current = true;
    setInputValue(value);
    if (onInputValueChanged) {
      onInputValueChanged(value);
    }
  }, []);

  useEffect(() => {
    if (debouncingInput.current && debouncedValue !== appliedSearchText) {
      onDebounceEnd(debouncedValue);
      debouncingInput.current = false;
    } else if (inputValue !== appliedSearchText && !debouncingInput.current) {
      setInputValue(appliedSearchText);
    }
  }, [debouncedValue, appliedSearchText, inputValue, onDebounceEnd]);

  return {
    inputValue,
    debouncedValue,
    onChange,
    stopDebouncing,
  };
};

type useDebouncedIdSearchProps = {
  searchTextFilterAtom: WritableAtom<string, [SetStateAction<string>], void>;
  onInputValueChange?: (value: string) => void;
};

export const useDebouncedIdSearch = ({
  searchTextFilterAtom,
  onInputValueChange,
}: useDebouncedIdSearchProps) => {
  const [appliedSearchText, setAppliedSearchText] = useAtom(searchTextFilterAtom);
  const { inputValue, onChange, stopDebouncing } = useFiltersSearchId({
    appliedSearchText,
    onInputValueChanged: onInputValueChange,
    onDebounceEnd: (value) => {
      setAppliedSearchText(value);
    },
  });

  return { inputValue, onChange, stopDebouncing };
};
